import { yupResolver } from '@hookform/resolvers/yup'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import React, { Suspense, useEffect, useEffectEvent, useMemo, useRef } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { useUnmount } from 'usehooks-ts'
import { ObjectSchema } from 'yup'

import { SystemDialogContent } from '@cms/components/data/Dialog'
import { AutoProgress } from '@cms/components/data/Progress'
import ReadyNodes from '@cms/components/editor/nodes/ReadyNodes'
import { EditorTopBarHeader, GeneralSettingsComponent } from '@cms/components/editor/shared/GeneralSettings'
import { LockStateGroup } from '@cms/components/editor/shared/LockStateGroup'
import { RestoreVersionButton } from '@cms/components/editor/shared/RestoreVersionButton'
import { SaveStoryButton } from '@cms/components/editor/shared/SaveStoryButton'
import { StoryOptionsMenu } from '@cms/components/editor/shared/StoryOptionsMenu'
import { StoryWorkflowButton } from "@cms/components/editor/shared/StoryWorkflowButton"
import EditorTheme from '@cms/components/editor/themes/EditorTheme'
import { contentElementsToLexical } from '@cms/components/editor/utils/helpers'
import { SystemBackdrop } from '@cms/components/ui/Backdrop'
import { useDialog } from '@cms/providers/DialogProvider'
import { useSnackbar } from '@cms/providers/SnackbarProvider'
import { SUBTYPE_OPTIONS } from '@cms/utils/constants'
import { socket } from '@cms/utils/socket'
import { hideTopBarMessageAction, showTopBarMessageAction } from '@cms/utils/store/slices/app.slice'
import { resetFormState } from '@cms/utils/store/slices/form.slice'
import { TEditorStoryVersion } from '@cms/utils/types'
import { checkRouteRequiredRole, COMMON_RESOURCES, IStoryLockReplyEvent, IStoryLockRequestEvent, IStoryLockStateEvent, ISystemStory, ISystemStoryLockState, ISystemTemplate, SOCKET_EVENTS, SUBTYPES } from '@dl/declarations'
import { ControlledDropdown } from '../../components/data/Dropdown'
import { StoryEditor } from '../../components/editor/Editor'
import { EditorWrapper } from '../../components/editor/EditorWrapper'
import { AddToCollectionMenu } from '../../components/editor/shared/AddToCollectionMenu'
import { PreviewDraftButton } from '../../components/editor/shared/PreviewDraftButton'
import { PreviewPublishedButton } from '../../components/editor/shared/PreviewPublishedButton'
import { TransferControlIcon } from '../../components/icons'
import { WrappedLoader } from '../../components/ui/Loader'
import PageContainer from '../../components/ui/PageContainer'
import api from '../../utils/api'
import { createStorySchema, generateStoryFormState, initialValues, StoryFormData } from '../../utils/forms/story'
import { generateSearchParam, renderDateTime } from '../../utils/helpers'
import { AssetsPath, Routes, StoriesPath, TemplatesPath } from '../../utils/routes'
import { useAppDispatch, useAppSelector } from '../../utils/store/hooks'
import { getSpecificStoryVersionAsyncAction, getStoryAsyncAction, resetEditorState, setEditorState } from '../../utils/store/slices/editor.slice'


export const EffectsComponent = () => {
    const dispatch = useAppDispatch()
    const { showSnackbar } = useSnackbar();
    const { openDialog, closeDialog } = useDialog();
    const [searchParams, setSearchParams] = useSearchParams()

    const { watch } = useFormContext<StoryFormData>()
    const intervalRef = useRef<number | null>(null);

    const story_id = searchParams.get(generateSearchParam(StoriesPath, "id"))
    const version_id = searchParams.get(generateSearchParam("versions", "id"))

    const [selected_subtype] = watch(['subtype']);
    const { profile: { _id: currentUserId } } = useAppSelector((state) => state.user)
    const { story, currentVersion, lastSavedVersionId, mode, mode_status } = useAppSelector((state) => state.editor)

    const onStoryLocked = useEffectEvent((lockedStory: ISystemStoryLockState) => {
        console.debug('onStoryLocked', lockedStory)

        const currentLockerId = story?.locked_by

        if (lockedStory._id !== story?._id) return;

        const newLockerId = lockedStory?.locked_by

        if (currentLockerId === newLockerId) return;

        dispatch(setEditorState({
            story: {
                ...story,
                ...lockedStory,
            } as TEditorStoryVersion
        }))

        if (!currentLockerId) return;

        const TRANSFER_TIME_IN_SECONDS = 3;

        openDialog({
            component: (
                <div
                    className={clsx(
                        'min-w-1/2 px-20 py-10 text-lg bg-white flex flex-col gap-4',
                        'rounded-lg shadow'
                    )}
                >
                    <div className='flex justify-center items-center gap-2'>
                        <p className='font-bold uppercase'>Transferring control of the article</p>
                        <TransferControlIcon />
                    </div>

                    <div className='text-center'>
                        {currentUserId === currentLockerId && (
                            <>
                                <p className='text-cs-500'>
                                    You are transferring control of the article.
                                </p>
                                <p>
                                    Please wait while we switch you to the article’s view mode.
                                </p>
                            </>
                        )}

                        {currentUserId === newLockerId && (
                            <>
                                <p className='text-cs-500'>
                                    You are taking control over the article.
                                </p>
                                <p>
                                    Please wait while we transfer you to the editable version.
                                </p>
                            </>
                        )}
                    </div>

                    <AutoProgress timeInSeconds={TRANSFER_TIME_IN_SECONDS} />
                </div>
            ),
            maxWidth: 'sm'
        })

        setTimeout(() => {
            closeDialog()
        }, TRANSFER_TIME_IN_SECONDS * 1000);
    })

    const onStoryUnlocked = useEffectEvent((unlockedStory: ISystemStoryLockState) => {
        console.debug('onStoryUnlocked', unlockedStory)

        if (unlockedStory._id !== story?._id) return;

        dispatch(setEditorState({
            story: {
                ...story,
                ...unlockedStory,
            } as TEditorStoryVersion
        }))
    })

    const onRequestLockStory = useEffectEvent((request: IStoryLockRequestEvent) => {
        console.debug('onRequestLockStory', request)

        const currentLockerId = story?.locked_by

        if (request?.story_id !== story?._id) return;

        const newLockerId = request?.requester_user?._id

        if (currentUserId !== currentLockerId) return;
        if (currentLockerId === newLockerId) return;

        openDialog({
            component: (
                <SystemDialogContent
                    dialogProps={{
                        title: 'Knocked!',
                    }}
                    content={{
                        component: (
                            <div className='p-2'>
                                <p>{request?.requester_user?.name} is attempting to take over this article. Can you confirm?</p>
                            </div>
                        )
                    }}
                    actionsProps={{
                        cancel: {
                            label: 'No',
                            onClick: () => {
                                socket.emit(SOCKET_EVENTS.REPLY_TO_LOCK_STORY, {
                                    story_id: story?._id,
                                    requester_user: request.requester_user,
                                    status: 'denied'
                                } as IStoryLockReplyEvent)

                                closeDialog()
                            }
                        },
                        submit: {
                            label: 'Yes',
                            onClick: () => {
                                socket.emit(SOCKET_EVENTS.REPLY_TO_LOCK_STORY, {
                                    story_id: story?._id,
                                    requester_user: request.requester_user,
                                    status: 'approved'
                                } as IStoryLockReplyEvent)

                                closeDialog()
                            }
                        }
                    }}
                />
            ),
            maxWidth: 'sm'
        })
    })

    const replyToLockStory = useEffectEvent((reply: IStoryLockReplyEvent) => {
        console.debug('replyToLockStory', reply)

        if (reply?.story_id !== story?._id) return;

        const newLockerId = reply?.requester_user?._id

        // Check if the current user is not the one who wants to take over the story
        if (currentUserId !== newLockerId) return;

        if (reply.status === 'approved') {
            socket.emit(SOCKET_EVENTS.LOCK_STORY, { story_id: story?._id, user_id: currentUserId })
        }
        else if (reply.status === 'denied') {
            dispatch(setEditorState({ mode_status: 'idle' }))
            showSnackbar('Request control of the article was denied.', 'warning');
        }
    })

    useEffect(() => {
        socket.on(SOCKET_EVENTS.STORY_LOCKED, (lockedStory: ISystemStoryLockState) => {
            onStoryLocked(lockedStory)
        })

        socket.on(SOCKET_EVENTS.STORY_UNLOCKED, (unlockedStory: ISystemStoryLockState) => {
            onStoryUnlocked(unlockedStory)
        })

        socket.on(SOCKET_EVENTS.REQUEST_LOCK_STORY, (request: IStoryLockRequestEvent) => {
            onRequestLockStory(request)
        })

        socket.on(SOCKET_EVENTS.REPLY_TO_LOCK_STORY, (reply: IStoryLockReplyEvent) => {
            replyToLockStory(reply)
        })

        return () => {
            socket.off(SOCKET_EVENTS.STORY_LOCKED)
            socket.off(SOCKET_EVENTS.STORY_UNLOCKED)
            socket.off(SOCKET_EVENTS.REQUEST_LOCK_STORY)
            socket.off(SOCKET_EVENTS.REPLY_TO_LOCK_STORY)
        }
    }, [])

    /** 
     * Set the editor mode based on the story lock status
    */
    useEffect(() => {
        const currentLockerId = story?.locked_by

        if (story) {
            if (
                story?.is_locked
                &&
                currentLockerId !== currentUserId
            ) {
                dispatch(setEditorState({ mode: 'readonly', mode_status: 'idle' }))
            }
            else {
                dispatch(setEditorState({ mode: 'editing', mode_status: 'idle' }))
                socket.emit(SOCKET_EVENTS.LOCK_STORY, { story_id: story._id, user_id: currentUserId } as IStoryLockStateEvent)

                intervalRef.current = window.setInterval(() => {
                    socket.emit(SOCKET_EVENTS.LOCK_STORY, { story_id: story._id, user_id: currentUserId } as IStoryLockStateEvent)
                }, 150_000); // 2.5 minutes
            }
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        }
    }, [story, currentUserId])

    useEffect(() => {
        if (selected_subtype) {
            const selected_subtype_slug = SUBTYPE_OPTIONS.find(item => item._id === selected_subtype)?.slug || null;
            if (selected_subtype_slug) {
                setSearchParams((prev) => {
                    prev.set(generateSearchParam(AssetsPath, "subtypes"), selected_subtype_slug)
                    return prev;
                }, {
                    replace: true
                })
            }
        }
    }, [selected_subtype])

    useEffect(() => {
        if (mode === 'readonly') {
            switch (mode_status) {
                case 'idle':
                    dispatch(showTopBarMessageAction({
                        type: 'info',
                        leftIcon: 'Lock',
                        content: `${(story?.lockedByObject?.name || 'Unknown User')} locked editing`,
                        pageId: Routes.EDITOR,
                    }));
                    break;

                case 'requesting-edit':
                    dispatch(showTopBarMessageAction({
                        type: 'warning',
                        leftIcon: 'TransferControl',
                        content: `You sent a request for control of the story to ${(story?.lockedByObject?.name || 'Unknown User')}`,
                        pageId: Routes.EDITOR,
                    }));
                    break;

                default:
                    dispatch(hideTopBarMessageAction({ pageId: Routes.EDITOR }));
                    break;
            }
        }
        else if (currentVersion && lastSavedVersionId && currentVersion._id !== lastSavedVersionId) {
            dispatch(showTopBarMessageAction({
                type: 'info',
                leftIcon: 'ViewLog',
                content: `Log Viewing: V${currentVersion.version} - ${renderDateTime(currentVersion.created_at)} - Status ${currentVersion.workflow}`,
                pageId: Routes.EDITOR,
            }));
        }
        else {
            dispatch(hideTopBarMessageAction({ pageId: Routes.EDITOR }));
        }
    }, [
        mode,
        mode_status,
        currentVersion,
        lastSavedVersionId,
    ])

    useEffect(() => {
        if (story_id && version_id) {
            dispatch(getSpecificStoryVersionAsyncAction({ storyId: story_id, versionId: version_id }))
        }
    }, [
        story_id,
        version_id,
    ])

    return null
}

export const StoryActions = () => {
    const { currentVersion, lastSavedVersionId } = useAppSelector((state) => state.editor)

    if (!lastSavedVersionId || currentVersion?._id === lastSavedVersionId) {
        return (
            <>
                <SaveStoryButton />
                <StoryWorkflowButton />
            </>
        )
    }

    return (
        <RestoreVersionButton />
    )
}

interface TopBarProps {
    actions?: {
        show?: boolean
    }
    subtypeOptions?: {
        isDisabled?: boolean,
        defaultSlugValue?: string,
    }
}

export const TopBar: React.FC<TopBarProps> = (props) => {
    const {
        actions = {
            show: true
        },
        subtypeOptions = {
            isDisabled: false,
            defaultSlugValue: SUBTYPES.NEWS.SLUG
        }
    } = props;

    const { story, mode } = useAppSelector((state) => state.editor)

    const { setValue } = useFormContext<StoryFormData>()

    const [searchParams] = useSearchParams()
    const template_id = searchParams.get(generateSearchParam(TemplatesPath, "id"))

    /** 
     * if no story and no template_id, set default subtype
    */
    useEffect(() => {
        if (!story && !template_id) {
            const defaultSubtype = [
                SUBTYPES.NEWS,
                SUBTYPES.RESEARCH,
                SUBTYPES.LEGAL,
            ].find(item => item.SLUG === subtypeOptions.defaultSlugValue)

            if (defaultSubtype) {
                setValue('subtype', defaultSubtype.ID);
            }
        }
    }, [subtypeOptions.defaultSlugValue, story, template_id])

    return (
        <div
            className={clsx(
                'sticky',
                'top-0',
                // hasTopBarMessage ? 'top-14' : 'top-0',
                'h-28 xl:h-14',
                'bg-white z-editor-general-settings w-full text-cs-700'
            )}

        >
            <div className='h-28 xl:h-14 px-4 py-2 flex flex-col md:flex-row items-center justify-between xl:gap-2 gap-1'>
                <EditorTopBarHeader />

                {actions.show && (
                    <>
                        {mode === 'readonly' ? (
                            <LockStateGroup />
                        ) : (
                            <div className='flex items-center divide-x divide-cs-300'>
                                <div className='relative flex flex-wrap items-center gap-2 ps-4'>
                                    <ControlledDropdown
                                        name='subtype'
                                        options={[
                                            ...SUBTYPE_OPTIONS,
                                            {
                                                _id: SUBTYPES.LEGAL.ID,
                                                slug: SUBTYPES.LEGAL.SLUG,
                                                label: SUBTYPES.LEGAL.NAME,
                                                groupKey: "Subtypes",
                                            }
                                        ]}
                                        label='Content type'
                                        valueKey='_id'
                                        inputSize='xs'
                                        disabled={subtypeOptions.isDisabled}
                                        searchOptions={{
                                            allow: false
                                        }}
                                        allowSingleDeselect={false}
                                    />

                                    <PreviewDraftButton />
                                    <PreviewPublishedButton />
                                    <AddToCollectionMenu />
                                    {!!story?._id && <StoryOptionsMenu />}
                                    <StoryActions />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

interface ComponentsStackProps {
    story: TEditorStoryVersion | null;

    topBarOptions?: {
        show?: boolean;
        options?: TopBarProps
    };
    editorComponentOptions?: {
        show?: boolean;
        options?: React.ComponentProps<typeof StoryEditor>
    };
    generalSettingsComponentOptions?: {
        show?: boolean;
        options?: React.ComponentProps<typeof GeneralSettingsComponent>;
    };
}

export const ComponentsStack: React.FC<ComponentsStackProps> = (props) => {
    const {
        story,
        topBarOptions = {
            show: true,
            options: {}
        },
        generalSettingsComponentOptions = {
            show: true,
            options: {
                presentationVersion: 'expanded'
            }
        },
        editorComponentOptions = {
            show: true,
            options: {}
        }
    } = props;

    const [searchParams] = useSearchParams()
    const [editor] = useLexicalComposerContext();

    const story_id = searchParams.get(generateSearchParam(StoriesPath, "id"))
    const template_id = searchParams.get(generateSearchParam(TemplatesPath, "id"))

    const methods = useForm<StoryFormData>({
        resolver: yupResolver(createStorySchema as unknown as ObjectSchema<StoryFormData>),
        defaultValues: story ? generateStoryFormState(story) : initialValues,
        mode: 'all'
    })

    const { data: template, isLoading: isTemplateLoading } = useQuery({
        queryKey: [TemplatesPath, template_id],
        queryFn: async () => {
            const res = await api.get<ISystemTemplate>(`/${TemplatesPath}/${template_id}`)
            return res.data
        },
        enabled: !!template_id && !story_id
    })

    useEffect(() => {
        // Template mode: no story_id but has template_id and template data
        if (!story_id && template_id && template) {
            methods.reset(generateStoryFormState(template as any));
            contentElementsToLexical(editor, template.content_elements);
            return;
        }

        // Story mode
        if (story) {
            methods.reset(generateStoryFormState(story));
            return;
        }

        // Default empty state
        methods.reset(initialValues);
    }, [story, story_id, template_id, template]);

    return (
        <Suspense
            fallback={(
                <WrappedLoader />
            )}
        >
            <FormProvider {...methods}>
                <SystemBackdrop isOpen={isTemplateLoading}>
                    <WrappedLoader loaderSize='lg' loaderColor='white' />
                </SystemBackdrop>

                <EffectsComponent />
                {topBarOptions.show && <TopBar {...topBarOptions.options} />}
                {generalSettingsComponentOptions.show && <GeneralSettingsComponent {...generalSettingsComponentOptions.options} />}
                {editorComponentOptions.show && <StoryEditor {...editorComponentOptions.options} />}
            </FormProvider>
        </Suspense>
    )
}

interface EditorPageProps extends Pick<ComponentsStackProps,
    "topBarOptions" |
    "editorComponentOptions" |
    "generalSettingsComponentOptions"
> { }

const EditorPage: React.FC<EditorPageProps> = (props) => {
    const [searchParams, setSearchParams] = useSearchParams()

    const dispatch = useAppDispatch()
    const { showSnackbar } = useSnackbar()

    const story_id = searchParams.get(generateSearchParam(StoriesPath, "id"))

    const { story, state, mode } = useAppSelector((state) => state.editor)
    const { profile: { _id: currentUserId, permissions: userPermissions } } = useAppSelector((state) => state.user)

    /**
     * Fetch story data when story_id changes
     * otherwise reset the editor state
    */
    useEffect(() => {
        if (story_id) {
            dispatch(getStoryAsyncAction({ id: story_id }))
                .then((payload) => {
                    if (payload.meta.requestStatus === 'fulfilled') {
                        const story = payload.payload as ISystemStory

                        if (!story._id) {
                            setSearchParams({}, { replace: true })

                            showSnackbar('Story with the provided ID not found', 'warning');

                            return;
                        }

                        const hasPermissions = checkRouteRequiredRole(COMMON_RESOURCES.STORIES.ROUTE, 'u', userPermissions)

                        if (!story.is_locked && hasPermissions) {
                            socket.emit(SOCKET_EVENTS.LOCK_STORY, { story_id: story._id, user_id: currentUserId } as IStoryLockStateEvent)
                        }

                        if (story.scheduleObject) {
                            showSnackbar(`This story has a scheduled action (${story.scheduleObject.workflow}) on ${renderDateTime(story.scheduleObject.action_at)}`, 'warning');
                        }
                    }
                })
        }
        else {
            dispatch(resetEditorState())
        }
    }, [story_id, currentUserId])

    useUnmount(() => {
        if (mode === 'editing') {
            socket.emit(SOCKET_EVENTS.UNLOCK_STORY, { story_id: story?._id, user_id: currentUserId })
        }

        dispatch(resetFormState())
        dispatch(resetEditorState())

        dispatch(hideTopBarMessageAction({ pageId: Routes.EDITOR }));
    })

    const renderContent = () => {
        if (state === "idle") {
            if (story_id && !story) {
                return null
            }
        }

        return (
            <EditorWrapper
                initialConfig={{
                    editorState: undefined,
                    namespace: 'StoryEditor',
                    nodes: [...ReadyNodes],
                    onError: (error: Error) => {
                        console.error('Lexical Editor Error:', error);
                        throw error;
                    },
                    theme: EditorTheme,
                }}
            >
                <ComponentsStack
                    story={story}
                    topBarOptions={{ show: true, options: { actions: { show: true }, subtypeOptions: { isDisabled: !!story?.published_story_version } } }}
                    {...props}
                />
            </EditorWrapper>
        )
    }

    const renderedContent = useMemo(() => {
        return renderContent()
    }, [story_id, story, state])

    return (
        <PageContainer
            className='overflow-x-clip px-0!'
            title={story?.headline}
        >
            <SystemBackdrop isOpen={state !== 'idle'}>
                <WrappedLoader loaderSize='lg' loaderColor='white' />
            </SystemBackdrop>

            {renderedContent}
        </PageContainer>
    )
}

export default EditorPage